import React from 'react';
import { twJoin } from 'tailwind-merge';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { TextImageBentoBoxTile } from './types/TextImageBentoBoxTile';

export default function TextImageBentoBox({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: TextImageBentoBoxTile;
  trackingPrefix?: string;
}>) {
  const textFullHeight = tile['text-full-height']?.value;
  const textCenter = tile['text-center']?.value;
  const minHeight = tile['min-height']?.options?.[0]?.value;

  let minHeightClasses;
  switch (minHeight) {
    case 'md':
      minHeightClasses = 'min-h-[200px] sm:min-h-[300px]';
      break;
    case 'lg':
      minHeightClasses = 'min-h-[300px] sm:min-h-[450px]';
      break;
    case 'xl':
      minHeightClasses = 'min-h-[400px] sm:min-h-[600px]';
      break;
    default:
      minHeightClasses = '';
      break;
  }

  let bgColorClass;
  switch (tile['bg-color']?.options?.[0]?.value) {
    case 'primary-5':
      bgColorClass = 'bg-primary-5';
      break;
    case 'neutral-5':
      bgColorClass = 'bg-neutral-5';
      break;
    default:
      bgColorClass = '';
      break;
  }

  let imageHeight;
  switch (tile['image-height']?.options?.[0]?.value) {
    case '100':
      imageHeight = 'h-full';
      break;
    case '80':
      imageHeight = 'h-4/5';
      break;
    case '50':
      imageHeight = 'h-2/5';
      break;
    default:
      imageHeight = 'h-full';
      break;
  }

  let textWidth;
  switch (tile['text-width']?.options?.[0]?.value) {
    case '100':
      textWidth = 'w-full';
      break;
    case '80':
      textWidth = 'w-full md:w-4/5';
      break;
    case '50':
      textWidth = 'w-full md:w-1/2';
      break;
    default:
      textWidth = 'w-full';
      break;
  }

  return (
    <div className={twJoin('h-full', bgColorClass, minHeightClasses)}>
      <div
        className={twJoin(
          'flex h-full flex-col md:flex-row',
          tile['image-left']?.value && 'md:flex-row-reverse',
        )}
      >
        <div
          className={twJoin(
            'p-5',
            textFullHeight && !textCenter
              ? 'flex h-full flex-col justify-between'
              : 'space-y-3 md:space-y-5',
            textCenter && 'my-auto',
            textWidth,
          )}
        >
          <RichTextRenderer blocks={tile.title} className="text-xl md:text-2xl xl:text-3xl" />
          <RichTextRenderer blocks={tile.subtitle} className="text-md md:text-lg xl:text-2xl" />
          <ButtonLink ctaComponent={tile.cta} className="w-fit" trackingPrefix={trackingPrefix} />
        </div>
        <OptimizedImage
          imageComponent={tile.image}
          className={twJoin('mx-auto my-auto max-h-fit w-auto md:max-w-[50%]', imageHeight)}
          width={300}
          height={300}
          sizes="300px"
        />
      </div>
    </div>
  );
}
